import { NavLink } from 'react-router-dom';
import { cn } from '@/lib/cn';
import { Icon } from '@/components/icons/Icon';
import { buyerNav, type NavItem } from './navItems';

/**
 * BottomNav — the mobile primary navigation.
 *
 * §11: "primary actions in the bottom third on mobile" and "touch targets ≥
 * 44px". Five destinations is the maximum that leaves each one a comfortable
 * thumb target at 393px, which is why Account absorbs everything secondary.
 *
 * On tablet and desktop this is replaced by SideRail, not merely stretched —
 * §11 again: "a tablet is not a stretched phone."
 */


export function BottomNav({ items = buyerNav }: { items?: NavItem[] }) {
  return (
    <nav
      aria-label="Primary"
      data-testid="bottom-nav"
      className="sticky bottom-0 z-20 flex border-t border-line bg-white pb-2.5 pt-2 md:hidden"
      style={{ paddingBottom: 'max(0.625rem, env(safe-area-inset-bottom))' }}
    >
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          className={({ isActive }) =>
            cn(
              'touch-target relative flex flex-1 flex-col items-center gap-[3px] text-[10px]',
              isActive ? 'font-bold text-blue' : 'text-faint',
            )
          }
        >
          {({ isActive }) => (
            <>
              <span className="relative">
                <Icon name={item.icon} size={20} strokeWidth={isActive ? 2 : 1.8} />
                {item.badge ? <NavBadge value={item.badge} /> : null}
              </span>
              {item.label}
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

function NavBadge({ value }: { value: number | true }) {
  if (value === true) {
    return (
      <span className="absolute -right-1 -top-0.5 h-[7px] w-[7px] rounded-full border-[1.5px] border-white bg-danger" />
    );
  }

  return (
    <span className="absolute -right-2.5 -top-1.5 rounded-pill bg-danger px-[5px] py-px text-[8.5px] font-bold text-white">
      {value > 99 ? '99+' : value}
    </span>
  );
}
